import React, { useEffect, useRef, useState } from 'react';
import { Download, Eye, FileText, Loader2, Plus, RefreshCw, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 20 * 1024 * 1024;
const MAX_GARANTES = 6;

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * VerazSection: subir y gestionar archivos PDF de Veraz vinculados a una Reserva o Contrato.
 * Props:
 *  - reservaId?: string
 *  - contratoId?: string
 *  - userRol?: string ('admin'|'analista'|'comercial'|'revisor'|'propietario')
 *  - disabled?: boolean (modo solo lectura)
 *  - defaultInquilinoNombre?: string (autocompleta el primer titular)
 */
export const VerazSection = ({
  reservaId,
  contratoId,
  userRol = 'analista',
  disabled = false,
  defaultInquilinoNombre = '',
}) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);
  // slots de garantes que el usuario tiene "abiertos" pero todavía sin pdf
  const [garanteSlots, setGaranteSlots] = useState([1]);
  // nombres ingresados manualmente para slots sin pdf
  const [pendingNames, setPendingNames] = useState({}); // { inquilino: '', garante_1: '', ... }
  const fileInputs = useRef({});

  const canUpload = ['admin', 'analista', 'comercial'].includes(userRol) && !disabled;
  const canDelete = ['admin', 'analista'].includes(userRol) && !disabled;

  const fetchDocs = async () => {
    if (!reservaId && !contratoId) return;
    setLoading(true);
    try {
      const url = contratoId
        ? `${API}/veraz/by-contrato/${contratoId}`
        : `${API}/veraz/by-reserva/${reservaId}`;
      const { data } = await axios.get(url, { headers: authHeaders() });
      setDocs(data || []);
      // abrir slots de garantes existentes
      const ordenes = (data || [])
        .filter(d => d.tipo === 'garante' && d.garante_orden)
        .map(d => d.garante_orden);
      const maxOrden = ordenes.length ? Math.max(...ordenes) : 0;
      setGaranteSlots(prev => {
        const all = new Set([...prev, ...ordenes, 1]);
        // garantizar slot extra para agregar uno nuevo
        if (maxOrden > 0) all.add(Math.min(maxOrden + 1, MAX_GARANTES));
        return Array.from(all).sort((a, b) => a - b);
      });
    } catch (err) {
      toast.error('No se pudieron cargar los Veraz');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
  }, [reservaId, contratoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const docInquilino = docs.find(d => d.tipo === 'inquilino');
  const docGarante = (orden) => docs.find(d => d.tipo === 'garante' && d.garante_orden === orden);

  const openFilePicker = (key) => {
    fileInputs.current[key]?.click();
  };

  const handleUpload = async (key, tipo, garanteOrden, file) => {
    if (!file) return;
    if (file.type && file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Solo se permiten archivos PDF');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('El archivo supera los 20 MB');
      return;
    }
    // Resolver titular_nombre
    const pendingKey = tipo === 'inquilino' ? 'inquilino' : `garante_${garanteOrden}`;
    const existing = tipo === 'inquilino' ? docInquilino : docGarante(garanteOrden);
    const titular_nombre = (
      pendingNames[pendingKey] ||
      existing?.titular_nombre ||
      (tipo === 'inquilino' ? defaultInquilinoNombre : '') ||
      ''
    ).trim();
    if (!titular_nombre) {
      toast.error(`Ingresá el nombre del ${tipo === 'inquilino' ? 'inquilino' : `garante ${garanteOrden}`} antes de subir el PDF`);
      return;
    }
    setUploadingKey(key);
    try {
      if (existing) {
        // Reemplazar
        const form = new FormData();
        form.append('file', file);
        await axios.put(`${API}/veraz/${existing.veraz_doc_id}/file`, form, { headers: { ...authHeaders() } });
        toast.success('PDF reemplazado');
      } else {
        const form = new FormData();
        form.append('file', file);
        form.append('tipo', tipo);
        form.append('titular_nombre', titular_nombre);
        if (reservaId) form.append('reserva_id', reservaId);
        if (contratoId) form.append('contrato_id', contratoId);
        if (garanteOrden) form.append('garante_orden', String(garanteOrden));
        await axios.post(`${API}/veraz/upload`, form, { headers: { ...authHeaders() } });
        toast.success('PDF cargado');
      }
      await fetchDocs();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Error al subir el PDF';
      toast.error(msg);
    } finally {
      setUploadingKey(null);
    }
  };

  const handleView = async (doc) => {
    try {
      const res = await axios.get(`${API}/veraz/${doc.veraz_doc_id}/file`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error('No se pudo abrir el PDF');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/veraz/${doc.veraz_doc_id}/file`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.archivo_nombre || 'veraz.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('No se pudo descargar el PDF');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`¿Eliminar el Veraz de "${doc.titular_nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/veraz/${doc.veraz_doc_id}`, { headers: authHeaders() });
      toast.success('Veraz eliminado');
      await fetchDocs();
    } catch (err) {
      toast.error('No se pudo eliminar');
    }
  };

  const handleRenameTitular = async (doc, newName) => {
    if (!newName.trim() || newName.trim() === doc.titular_nombre) return;
    try {
      await axios.patch(`${API}/veraz/${doc.veraz_doc_id}`, { titular_nombre: newName.trim() }, { headers: authHeaders() });
      await fetchDocs();
    } catch (err) {
      toast.error('No se pudo renombrar');
    }
  };

  const addGaranteSlot = () => {
    setGaranteSlots(prev => {
      if (prev.length >= MAX_GARANTES) return prev;
      const next = Math.max(...prev, 0) + 1;
      return [...prev, next];
    });
  };

  const removeGaranteSlot = (orden) => {
    setGaranteSlots(prev => prev.filter(o => o !== orden));
    setPendingNames(p => {
      const np = { ...p }; delete np[`garante_${orden}`]; return np;
    });
  };

  const renderSlot = ({ key, label, tipo, orden, existingDoc, pendingNameKey }) => {
    const isUploading = uploadingKey === key;
    const displayName = existingDoc?.titular_nombre ?? (pendingNames[pendingNameKey] ?? (tipo === 'inquilino' ? defaultInquilinoNombre : ''));

    return (
      <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`veraz-slot-${key}`}>
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-slate-500 shrink-0" />
            <span className="text-sm font-medium text-slate-700">{label}</span>
            {existingDoc && (
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                Con PDF
              </span>
            )}
          </div>
          {tipo === 'garante' && !existingDoc && canUpload && garanteSlots.length > 1 && (
            <button
              type="button"
              className="text-xs text-slate-400 hover:text-rose-600"
              onClick={() => removeGaranteSlot(orden)}
              data-testid={`veraz-quitar-garante-${orden}`}
            >
              Quitar
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Nombre del titular</Label>
            <Input
              type="text"
              value={displayName}
              disabled={!canUpload && !existingDoc}
              onChange={(e) => {
                if (existingDoc) {
                  // Cambio "in place": no llamamos a PATCH hasta blur
                  setPendingNames(p => ({ ...p, [pendingNameKey]: e.target.value }));
                } else {
                  setPendingNames(p => ({ ...p, [pendingNameKey]: e.target.value }));
                }
              }}
              onBlur={(e) => {
                if (existingDoc && e.target.value.trim() && e.target.value.trim() !== existingDoc.titular_nombre) {
                  handleRenameTitular(existingDoc, e.target.value);
                }
              }}
              placeholder={tipo === 'inquilino' ? 'Razón social o nombre' : 'Nombre del garante'}
              className="h-8 text-sm"
              data-testid={`veraz-titular-${key}`}
            />
          </div>

          <div className="flex items-center gap-1">
            <input
              ref={el => (fileInputs.current[key] = el)}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(key, tipo, orden, f);
                e.target.value = '';
              }}
              data-testid={`veraz-input-${key}`}
            />
            {existingDoc ? (
              <>
                <Button type="button" size="sm" variant="outline" className="h-8 px-2" onClick={() => handleView(existingDoc)} data-testid={`veraz-ver-${key}`}>
                  <Eye className="w-4 h-4" />
                </Button>
                <Button type="button" size="sm" variant="outline" className="h-8 px-2" onClick={() => handleDownload(existingDoc)} data-testid={`veraz-descargar-${key}`}>
                  <Download className="w-4 h-4" />
                </Button>
                {canUpload && (
                  <Button type="button" size="sm" variant="outline" className="h-8 px-2" onClick={() => openFilePicker(key)} disabled={isUploading} data-testid={`veraz-reemplazar-${key}`} title="Reemplazar">
                    {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                )}
                {canDelete && (
                  <Button type="button" size="sm" variant="outline" className="h-8 px-2 text-rose-600 hover:bg-rose-50" onClick={() => handleDelete(existingDoc)} data-testid={`veraz-eliminar-${key}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </>
            ) : (
              canUpload && (
                <Button type="button" size="sm" className="h-8" onClick={() => openFilePicker(key)} disabled={isUploading} data-testid={`veraz-subir-${key}`}>
                  {isUploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  Subir PDF
                </Button>
              )
            )}
          </div>
        </div>

        {existingDoc && (
          <div className="mt-2 text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
            <span title={existingDoc.archivo_nombre}>{existingDoc.archivo_nombre}</span>
            <span>·</span>
            <span>{(existingDoc.tamano_bytes / 1024).toFixed(0)} KB</span>
            <span>·</span>
            <span>Cargado {new Date(existingDoc.fecha_carga).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}</span>
            {existingDoc.usuario_nombre && (<><span>·</span><span>por {existingDoc.usuario_nombre}</span></>)}
          </div>
        )}
      </div>
    );
  };

  if (!reservaId && !contratoId) {
    return (
      <div className="text-sm text-slate-500 italic p-3 bg-slate-50 rounded">
        Guardá la reserva o contrato primero para poder adjuntar archivos Veraz.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="veraz-section">
      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando Veraz…
        </div>
      )}

      {/* Inquilino */}
      {renderSlot({
        key: 'inquilino',
        label: 'Veraz Inquilino',
        tipo: 'inquilino',
        orden: null,
        existingDoc: docInquilino,
        pendingNameKey: 'inquilino',
      })}

      {/* Garantes */}
      {garanteSlots.map((orden) => (
        <React.Fragment key={`g-${orden}`}>
          {renderSlot({
            key: `garante_${orden}`,
            label: `Veraz Garante ${orden}`,
            tipo: 'garante',
            orden,
            existingDoc: docGarante(orden),
            pendingNameKey: `garante_${orden}`,
          })}
        </React.Fragment>
      ))}

      {canUpload && garanteSlots.length < MAX_GARANTES && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addGaranteSlot}
          className="w-full sm:w-auto"
          data-testid="veraz-agregar-garante"
        >
          <Plus className="w-4 h-4 mr-1" /> Agregar Garante
        </Button>
      )}

      <p className="text-[11px] text-slate-400">
        Solo PDF · máximo 20 MB por archivo. Los Veraz cargados en la reserva se conservan al convertirla en contrato.
      </p>
    </div>
  );
};

export default VerazSection;
